// Umami analytics, per instance (settings.analytics): only the UdN measures,
// on its own self-hosted Umami. Everywhere else no script is loaded, so every
// call here is a no-op.
//
// Kept within the CNIL audience-measurement exemption (#94, Q15-Q17): no
// cookie, no identify (the visits are never tied to a person), no website id
// shared with another site, Do Not Track honoured, query strings and record
// ids left out of the URLs (window.rolebaseTrackUrl, index.html), data kept
// 25 months at most (purge on the Umami server).
//
// Heatmaps (03/10/2026): recorder.js, from the same Umami, records where people
// click and how far they scroll — positions and page sizes, no text, no screen.
// The Umami server decides what it records: session replays are switched off
// there, and must stay off without the members' consent. It needs the
// tracker's session, so Do Not Track still holds.
//
// Naming convention: `domain_object_action`, snake_case, past tense once the
// action is done (`subscription_plan_selected`). The domain matches the webapp
// feature folder. An event name that is a funnel step in Umami cannot be
// renamed without breaking the report.

export type TrackProps = Record<
  string,
  string | number | boolean | undefined | null
>

declare global {
  interface Window {
    umami?: {
      track(name: string, data?: TrackProps): void
    }
  }
}

// Umami rejects null values, and empty strings only add noise to the reports.
function clean(props?: TrackProps): TrackProps | undefined {
  if (!props) return undefined
  const entries = Object.entries(props).filter(
    ([, value]) => value !== undefined && value !== null && value !== ''
  )
  return entries.length ? Object.fromEntries(entries) : undefined
}

export function track(name: string, props?: TrackProps) {
  window.umami?.track(name, clean(props))
}

export interface AnalyticsConfig {
  scriptUrl: string
  websiteId: string
  // Only this host is measured, even if the bundle is served elsewhere
  domain: string
  // Heatmap recorder of the same Umami; absent = no heatmaps
  recorderUrl?: string
}

// Load the tracker of the instance, once. Returns the script element, or
// undefined when the instance does not measure.
export function loadAnalytics(
  config: AnalyticsConfig | null,
  doc: Document = document
): HTMLScriptElement | undefined {
  if (!config) return undefined
  const existing = doc.querySelector<HTMLScriptElement>(
    'script[data-website-id]'
  )
  if (existing) return existing

  const script = doc.createElement('script')
  script.defer = true
  script.src = config.scriptUrl
  script.dataset.websiteId = config.websiteId
  script.dataset.domains = config.domain
  script.dataset.doNotTrack = 'true'
  // Invitation links carry ?memberId=&token=, and the reset-password return
  // carries ?email=. None of it belongs in analytics.
  script.dataset.excludeSearch = 'true'
  // Record ids replaced by :id, org slugs by :slug (index.html)
  script.dataset.beforeSend = 'rolebaseTrackUrl'
  doc.head.appendChild(script)

  // recorder.js sends the raw address, without rolebaseTrackUrl: an invitation
  // link (?memberId=&token=) or a password link (?email=) would reach Umami.
  // Those links always open a fresh page, so the check at load is enough.
  if (config.recorderUrl && !doc.location?.search) {
    const recorder = doc.createElement('script')
    recorder.defer = true
    recorder.src = config.recorderUrl
    recorder.dataset.websiteId = config.websiteId
    doc.head.appendChild(recorder)
  }
  return script
}
